/**
 * uploadTableFile — sube un Excel/CSV adjunto al chat a S3 (presigned + PUT) y
 * devuelve su s3_key. A diferencia de uploadOcrDocument, NO crea un ocr_scan ni
 * lo manda al worker de visión: los datos tabulares se importan vía la tool
 * `importar_tabla` del backend, que descarga el fichero por su s3_key.
 */

import { apiFetch } from '@/utils/api';

export const TABLE_ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'text/csv',
  'application/csv',
];

export const TABLE_MAX_BYTES = 5 * 1024 * 1024;

export function isTableFile(file) {
  if (!file) return false;
  if (TABLE_ACCEPTED_TYPES.includes(file.type)) return true;
  // El navegador a veces no reporta MIME para .csv/.xls → mirar extensión.
  return /\.(xlsx|xls|csv)$/i.test(file.name || '');
}

/**
 * @returns {Promise<{ s3_key: string, filename: string, content_type: string }>}
 */
/**
 * `scope` decide DÓNDE aterriza el fichero y con qué permiso se autoriza la subida.
 *
 * Sin él, la API cae a su scope por defecto, que sin `project_id` escribe en
 * `expenses/<empresa>/`: la carpeta de los justificantes de gasto. Ahí acabaron los
 * extractos bancarios hasta el 17-09-2026, autorizados además con `accounting.edit`
 * en vez de con el permiso de la ruta que después los lee.
 *
 * Los adjuntos del chat de ARIA no pasan `scope` a propósito: son ficheros de trabajo
 * de una conversación, no documentación contable con plazo de conservación.
 */
export async function uploadTableFile(file, { scope } = {}) {
  if (!isTableFile(file)) throw new Error('Formato no admitido. Usa Excel (.xlsx/.xls) o CSV.');
  if (file.size > TABLE_MAX_BYTES) throw new Error('El fichero supera el límite de 5 MB.');

  const presigned = await apiFetch('/api/functions/presigned-upload', {
    method: 'POST',
    body: JSON.stringify({
      file_name: file.name,
      file_type: file.type || 'application/octet-stream',
      ...(scope ? { scope } : {}),
    }),
  });
  const uploadUrl = presigned?.uploadUrl;
  const key = presigned?.key;
  if (!uploadUrl || !key) throw new Error('No se pudo preparar la subida del archivo.');

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      // La URL firmada lleva el tagging DENTRO cuando la API lo ha puesto. Si el PUT
      // no manda uno idéntico, **S3 rechaza la subida**. Se manda el que ha venido en
      // la respuesta en vez de repetir aquí la constante: así no pueden separarse.
      ...(presigned?.tagging ? { 'x-amz-tagging': presigned.tagging } : {}),
    },
    body: file,
  });
  if (!putRes.ok) throw new Error('Falló la subida del archivo a almacenamiento.');

  return { s3_key: key, filename: file.name, content_type: file.type || '' };
}
